import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Icon,
  Button,
  List,
  ListItem,
  ListIcon,
  Stack,
} from '@chakra-ui/react';
import {
  FaPlay,
  FaCheckCircle,
  FaUserFriends,
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';
import TeamMember from '../components/TeamMember';
import ScrollIndicator from '../components/ScrollIndicator';
import PageHero from '../components/PageHero';
import ScrollReveal from '../components/ScrollReveal';

const About = () => {
  return (
    <>
      <SEO
        title="About Us - Namaskar India"
        description="Meet the team behind Namaskar India podcast, bringing you stories from Indian history and mythology."
      />

      <Box minH="100vh" pt={{ base: 20, md: 20 }} id="about-container">
        <PageHero
          title="About Namaskar India"
          subtitle="Bringing the stories that shaped Indian civilization to life through immersive storytelling."
        />
        <ScrollIndicator containerId="about-container" />

        {/* Main Content */}
        <Container maxW="container.xl" px={{ base: 4, md: 8 }} py={{ base: 6, md: 8 }}>
          <VStack spacing={{ base: 8, md: 12 }} align="center" px={{ base: 4, md: 0 }}>
            {/* Team Section */}
            <VStack spacing={{ base: 4, md: 6 }} w="full" maxW="6xl">
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "4xl" }}
                variant="gradient"
                textAlign="center"
                mb={2}
              >
                Meet the Team
              </Heading>

              <SimpleGrid
                columns={{ base: 1, lg: 2 }}
                spacing={{ base: 6, md: 8 }}
                w="full"
              >
                <ScrollReveal delay={0}>
                  <TeamMember
                    name="Aradhana Duppala"
                    role="Host & Content Creator"
                    bio="An Engineering Manager by profession, Aradhana is a passionate history and mythology enthusiast who brings ancient tales to life through engaging storytelling. Her deep love for India's cultural heritage, combined with her technical background, allows her to present complex historical narratives in an accessible and engaging way for modern audiences."
                    image="/host-image.jpg"
                    social={{
                      linkedin: "https://www.linkedin.com/in/aradhanaduppala",
                      twitter: "https://twitter.com/AradhanaDuppala",
                      instagram: "https://www.instagram.com/aduppala"
                    }}
                  />
                </ScrollReveal>
                <ScrollReveal delay={0.08}>
                  <TeamMember
                    name="Shivansh Singh"
                    role="Production Manager"
                    bio="A Principal Solutions Architect by profession, Shivansh brings his technical expertise to Namaskar India driven by his deep love for Indian culture and storytelling. His passion for preserving and sharing India's rich heritage, combined with his technical skills, ensures our stories reach listeners with professional quality and authentic presentation."
                    image="/images/team/shivansh.jpg"
                    social={{
                      linkedin: "https://www.linkedin.com/in/singhshivansh",
                      twitter: "https://twitter.com/shivansh_singh",
                      instagram: "https://www.instagram.com/shivansh"
                    }}
                  />
                </ScrollReveal>
              </SimpleGrid>
            </VStack>

            {/* Mission Section */}
            <ScrollReveal>
              <VStack spacing={{ base: 4, md: 6 }} w="full" maxW="4xl">
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "4xl" }}
                  variant="gradient"
                  textAlign="center"
                  mb={2}
                >
                  Our Mission
                </Heading>

                <Box
                  layerStyle="glassCard"
                  p={{ base: 6, md: 8 }}
                  w="full"
                  position="relative"
                  overflow="hidden"
                  _hover={{ borderColor: 'rgba(255,152,0,0.35)', boxShadow: '0 12px 40px rgba(255,152,0,0.08)' }}
                  transition="all 0.3s"
                >
                  <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, brand.500, transparent)" />
                  <List spacing={{ base: 3, md: 4 }} w="full" color="whiteAlpha.900">
                    <ListItem>
                      <ListIcon as={FaCheckCircle} color="brand.500" />
                      At Namaskar India, we're passionate about making Indian history and mythology accessible to everyone.
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FaCheckCircle} color="brand.500" />
                      Through carefully researched episodes and engaging storytelling, we aim to preserve and share the rich cultural heritage of India with audiences worldwide.
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FaCheckCircle} color="brand.500" />
                      Our goal is to bridge the past with the present, helping listeners understand the depth and relevance of India's ancient wisdom in today's world.
                    </ListItem>
                  </List>
                </Box>
              </VStack>
            </ScrollReveal>

            {/* What We Do Section */}
            <VStack spacing={{ base: 6, md: 10 }} w="full" maxW="6xl">
              <ScrollReveal>
                <VStack spacing={{ base: 3, md: 4 }}>
                  <Heading
                    as="h2"
                    fontSize={{ base: "2xl", md: "4xl" }}
                    variant="gradient"
                    textAlign="center"
                    mb={2}
                  >
                    What We Do
                  </Heading>
                  <Text fontSize={{ base: "sm", md: "lg" }} color="whiteAlpha.900" textAlign="center">
                    Through carefully researched episodes, we explore:
                  </Text>
                </VStack>
              </ScrollReveal>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }} w="full" maxW="4xl">
                <ScrollReveal delay={0}>
                  <VStack
                    align="start"
                    spacing={4}
                    layerStyle="glassCard"
                    p={{ base: 6, md: 8 }}
                    position="relative"
                    overflow="hidden"
                    height="100%"
                    _hover={{ borderColor: 'rgba(255,152,0,0.35)', boxShadow: '0 12px 40px rgba(255,152,0,0.08)' }}
                    transition="all 0.3s"
                  >
                    <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, brand.500, transparent)" />
                    <Heading size="md" color="brand.300">Historical Series</Heading>
                    <List spacing={3} color="whiteAlpha.900">
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="brand.500" />
                        Ancient Indian civilizations and empires
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="brand.500" />
                        Cultural evolution and traditions
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="brand.500" />
                        Significant historical events
                      </ListItem>
                    </List>
                  </VStack>
                </ScrollReveal>

                <ScrollReveal delay={0.08}>
                  <VStack
                    align="start"
                    spacing={4}
                    layerStyle="glassCard"
                    p={{ base: 6, md: 8 }}
                    position="relative"
                    overflow="hidden"
                    height="100%"
                    _hover={{ borderColor: 'rgba(255,152,0,0.35)', boxShadow: '0 12px 40px rgba(255,152,0,0.08)' }}
                    transition="all 0.3s"
                  >
                    <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, terracotta.500, transparent)" />
                    <Heading size="md" color="brand.300">Mythology Series</Heading>
                    <List spacing={3} color="whiteAlpha.900">
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="brand.500" />
                        Stories from ancient texts and epics
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="brand.500" />
                        Philosophical concepts and teachings
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="brand.500" />
                        Cultural impact and modern relevance
                      </ListItem>
                    </List>
                  </VStack>
                </ScrollReveal>
              </SimpleGrid>
            </VStack>

            {/* Listen Now Section */}
            <ScrollReveal>
              <Box
                w="full"
                layerStyle="glassCard"
                p={{ base: 6, md: 12 }}
                position="relative"
                overflow="hidden"
              >
                <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, brand.500, terracotta.500, transparent)" />
                <VStack spacing={{ base: 4, md: 8 }} align="center" textAlign="center">
                  <Icon as={FaUserFriends} boxSize={{ base: 8, md: 12 }} color="brand.500" />
                  <Heading
                    as="h2"
                    fontSize={{ base: "2xl", md: "4xl" }}
                    variant="gradient"
                    textAlign="center"
                  >
                    Join Our Community
                  </Heading>
                  <Text fontSize={{ base: "xs", md: "lg" }} color="whiteAlpha.800" maxW="2xl">
                    Be part of a growing community rediscovering the stories that shaped Indian culture and civilization.
                  </Text>
                  <Stack
                    direction={{ base: "column", sm: "row" }}
                    spacing={{ base: 3, sm: 4 }}
                    pt={{ base: 2, md: 4 }}
                    width="100%"
                    px={{ base: 4, md: 0 }}
                    align={{ base: 'stretch', sm: 'center' }}
                    justify="center"
                  >
                    <Button
                      as={RouterLink}
                      to="/episodes"
                      colorScheme="brand"
                      size="lg"
                      leftIcon={<Icon as={FaPlay} />}
                      _hover={{
                        transform: 'translateY(-2px)',
                        shadow: 'lg',
                      }}
                      transition="all 0.2s"
                      width={{ base: "100%", sm: "auto" }}
                    >
                      Start Listening
                    </Button>
                    <Button
                      as={RouterLink}
                      to="/contact"
                      variant="outline"
                      colorScheme="whiteAlpha"
                      size="lg"
                      _hover={{
                        transform: 'translateY(-2px)',
                        shadow: 'lg',
                      }}
                      transition="all 0.2s"
                      width={{ base: "100%", sm: "auto" }}
                    >
                      Get in Touch
                    </Button>
                  </Stack>
                </VStack>
              </Box>
            </ScrollReveal>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default About;
